import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface Standard {
  id?: string;
  displayName: string;
  classroomId?: number;
  levelId?: number;
  assessedAreaId: number;
  assessedArea?: {
    id: number;
    displayName: string;
  };
  level?: {
    id: number;
    displayName: string;
  };
  classroom?: {
    id: number;
    displayName: string;
  };
  actions?: {
    canEdit: boolean;
    canDelete: boolean;
  };
}

export interface AssessedArea {
  id: number;
  displayName: string;
  description?: string;
  level?: {
    id: number;
    displayName: string;
  };
  classroom?: {
    id: number;
    displayName: string;
  };
}

export const StandardsApi = createApi({
  reducerPath: 'StandardsApi',
  baseQuery,
  tagTypes: ['standards'],
  endpoints: builder => ({
    getStandards: builder.query<
      { data: Standard[] },
      {
        classroomId?: string;
        levelId?: string;
        assessedAreaId?: string;
        search?: string;
      }
    >({
      query: ({ classroomId, levelId, assessedAreaId, search }) => {
        let url = '/academics/standards';
        const params = new URLSearchParams();
        if (classroomId) {
          params.append('classroomId', classroomId);
        }
        if (levelId) {
          params.append('levelId', levelId);
        }
        if (search) {
          params.append('q', search);
        }
        if (assessedAreaId) {
          params.append('assessedAreaId', assessedAreaId);
        }
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        return url;
      },
      providesTags: ['standards'],
    }),
    getAssessedAreas: builder.query<
      { data: AssessedArea[] },
      { classroomId?: string; levelId?: string } | void
    >({
      query: (params) => {
        let url = '/academics/assessed-areas';
        const searchParams = new URLSearchParams();
        if (params?.classroomId) {
          searchParams.append('classroomId', params.classroomId);
        }
        if (params?.levelId) {
          searchParams.append('levelId', params.levelId);
        }
        if (searchParams.toString()) {
          url += `?${searchParams.toString()}`;
        }
        return url;
      },
    }),
    createStandard: builder.mutation<Standard, Omit<Standard, 'id'>>({
      query: standard => ({
        url: '/academics/standards',
        method: 'POST',
        body: standard,
      }),
      invalidatesTags: ['standards'],
    }),
    updateStandard: builder.mutation<
      Standard,
      { id: string; displayName?: string; levelId?: number; classroomId?: number }
    >({
      query: ({ id, ...updates }) => ({
        url: `/academics/standards/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['standards'],
    }),
    deleteStandard: builder.mutation<void, string>({
      query: id => ({
        url: `/academics/standards/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['standards'],
    }),
  }),
});

export const {
  useGetStandardsQuery,
  useCreateStandardMutation,
  useUpdateStandardMutation,
  useDeleteStandardMutation,
  useGetAssessedAreasQuery,
} = StandardsApi;
